/**
 * QuietMind Audio System
 * Based on DES-033: Sound Design
 *
 * Philosophy: Sounds should feel INTERNAL — like the gentle sounds
 * inside your own head when processing, not external game effects.
 * Sound is off by default (calm by default principle).
 */

export type SoundType = 'flip' | 'hard' | 'medium' | 'easy' | 'skip' | 'improved' | 'complete';
export type Tempo = 'slow' | 'normal' | 'fast';

interface QuietMindOptions {
  /** Whether sound is enabled */
  enabled?: boolean;
  /** Base volume (0-1) */
  volume?: number;
  /** Whether to use dark mode adjustments */
  darkMode?: boolean;
}

/**
 * QuietMind audio synthesizer for flashcard feedback.
 * Uses Web Audio API for real-time sound generation.
 */
export class QuietMind {
  private audioContext: AudioContext | null = null;
  private enabled: boolean;
  private volume: number;
  private darkMode: boolean;
  private lastSoundTime: number = 0;

  constructor(options: QuietMindOptions = {}) {
    this.enabled = options.enabled ?? false;
    this.volume = options.volume ?? 0.3;
    this.darkMode = options.darkMode ?? false;
  }

  /**
   * Initialize audio context (must be called after user interaction)
   */
  init(): void {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
  }

  /**
   * Enable or disable sounds
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.init();
    }
  }

  /**
   * Set volume (0-1)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Set dark mode (affects sound characteristics)
   */
  setDarkMode(darkMode: boolean): void {
    this.darkMode = darkMode;
  }

  /**
   * Play a sound
   */
  play(type: SoundType, tempo: Tempo = 'normal'): void {
    if (!this.enabled || !this.audioContext) return;

    // Sounds never compete: wait for previous to reach 20% volume
    const now = performance.now();
    const minGap = 100; // ms
    if (now - this.lastSoundTime < minGap) return;
    this.lastSoundTime = now;

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    switch (type) {
      case 'flip':
        this.playFlip();
        break;
      case 'hard':
        this.playRating('hard', tempo);
        break;
      case 'medium':
        this.playRating('medium', tempo);
        break;
      case 'easy':
        this.playRating('easy', tempo);
        break;
      case 'skip':
        this.playSkip();
        break;
      case 'improved':
        this.playImproved();
        break;
      case 'complete':
        this.playComplete();
        break;
    }
  }

  /**
   * Card flip sound: Soft click
   */
  private playFlip(): void {
    const ctx = this.audioContext!;
    const now = ctx.currentTime;

    // Very short white noise click
    const bufferSize = ctx.sampleRate * 0.05;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1000;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(this.volume * 0.1, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(now);
    noise.stop(now + 0.05);
  }

  /**
   * Rating sounds: Warm tones that feel equally positive
   */
  private playRating(rating: 'hard' | 'medium' | 'easy', tempo: Tempo): void {
    const ctx = this.audioContext!;
    const now = ctx.currentTime;

    // Frequencies based on DES-033
    const frequencies: Record<string, number> = {
      hard: 130.81, // C3 - grounding, warm
      medium: 196.0, // G3 - clear, neutral
      easy: 261.63, // C4 - bright, releasing
    };

    // Tempo affects pitch
    const pitchShift = tempo === 'fast' ? 1.05 : tempo === 'slow' ? 0.95 : 1.0;

    // Dark mode: lower frequencies
    const darkModeShift = this.darkMode ? 0.9 : 1.0;

    const freq = frequencies[rating] * pitchShift * darkModeShift;

    // Soft mallet percussion synthesis
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;

    // Add subtle harmonic
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = freq * 2;

    const gain = ctx.createGain();
    const gain2 = ctx.createGain();
    const masterGain = ctx.createGain();

    // Soft attack (emergence from silence)
    const attackTime = 0.05;
    const decayTime = this.darkMode ? 0.5 : 0.4; // Longer decay in dark mode

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(this.volume * 0.25, now + attackTime);
    gain.gain.exponentialRampToValueAtTime(0.001, now + decayTime);

    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(this.volume * 0.1, now + attackTime);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + decayTime * 0.8);

    masterGain.gain.value = 1;

    osc.connect(gain);
    osc2.connect(gain2);
    gain.connect(masterGain);
    gain2.connect(masterGain);
    masterGain.connect(ctx.destination);

    const startDelay = 0.05; // Slight delay (emergence from silence)
    osc.start(now + startDelay);
    osc2.start(now + startDelay);
    osc.stop(now + decayTime + 0.1);
    osc2.stop(now + decayTime + 0.1);
  }

  /**
   * Skip sound: Soft breath + paper rustle
   */
  private playSkip(): void {
    const ctx = this.audioContext!;
    const now = ctx.currentTime;

    // White noise for paper rustle
    const bufferSize = ctx.sampleRate * 0.12;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;

    // Bandpass filter for paper-like sound
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000;
    filter.Q.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(this.volume * 0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(now);
    noise.stop(now + 0.12);
  }

  /**
   * Card improved sound: Ascending interval (G4 → C5)
   */
  private playImproved(): void {
    const ctx = this.audioContext!;
    const now = ctx.currentTime;

    const darkModeShift = this.darkMode ? 0.9 : 1.0;
    const freq1 = 392.0 * darkModeShift; // G4
    const freq2 = 523.25 * darkModeShift; // C5

    // First note
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = freq1;

    const gain1 = ctx.createGain();
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(this.volume * 0.2, now + 0.05);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    // Second note (delayed)
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = freq2;

    const gain2 = ctx.createGain();
    gain2.gain.setValueAtTime(0, now + 0.15);
    gain2.gain.linearRampToValueAtTime(this.volume * 0.2, now + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

    osc1.connect(gain1);
    osc2.connect(gain2);
    gain1.connect(ctx.destination);
    gain2.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now + 0.15);
    osc1.stop(now + 0.35);
    osc2.stop(now + 0.55);
  }

  /**
   * Session complete: Settling arpeggio (descending C-G-E-C)
   */
  private playComplete(): void {
    const ctx = this.audioContext!;
    const now = ctx.currentTime;

    const darkModeShift = this.darkMode ? 0.9 : 1.0;
    const notes = [
      523.25, // C5
      392.0, // G4
      329.63, // E4
      261.63, // C4
    ].map(f => f * darkModeShift);

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const gain = ctx.createGain();
      const startTime = now + i * 0.15;
      const decayTime = this.darkMode ? 0.5 : 0.4;

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(this.volume * 0.15, startTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + decayTime);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + decayTime + 0.1);
    });
  }

  /**
   * Clean up audio context
   */
  dispose(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Singleton instance
let instance: QuietMind | null = null;

/**
 * Get the QuietMind singleton instance
 */
export function getQuietMind(): QuietMind {
  if (!instance) {
    instance = new QuietMind();
  }
  return instance;
}
